import React, { useState, useEffect } from 'react';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useNavigate, useParams } from 'react-router-dom';
import './AjoutArticle.css'; // On réutilise le même CSS

function ModifierArticle() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [titre, setTitre] = useState('');
  const [resume, setResume] = useState('');
  const [contenuComplet, setContenuComplet] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [newImage, setNewImage] = useState<File | null>(null);
  const [positionImage, setPositionImage] = useState<'gauche' | 'droite' | 'aucune'>('aucune');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchArticle = async () => {
      if (!id) return;

      try {
        const db = getFirestore();
        const articleDoc = await getDoc(doc(db, 'Articles', id));
        if (articleDoc.exists()) {
          const data = articleDoc.data();
          setTitre(data.titre);
          setResume(data.resume);
          setContenuComplet(data.contenuComplet);
          setImageUrl(data.image || '');
          setPositionImage(data.positionImage || 'aucune');
        }
      } catch (err) {
        console.error('Erreur lors de la récupération de l\'article:', err);
        setError('Erreur lors de la récupération de l\'article');
      }
    };

    fetchArticle();
  }, [id]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setNewImage(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setLoading(true);
    setError('');

    try {
      let updatedImageUrl = imageUrl;

      // Si une nouvelle image est sélectionnée
      if (newImage && positionImage !== 'aucune') {
        const storage = getStorage();

        // Supprimer l'ancienne image si elle existe
        if (imageUrl) {
          try {
            const oldImageRef = ref(storage, imageUrl);
            await deleteObject(oldImageRef);
          } catch (error) {
            console.error('Erreur lors de la suppression de l\'ancienne image:', error);
          }
        }

        // Uploader la nouvelle image
        const timestamp = Date.now();
        const imageRef = ref(storage, `articles/${timestamp}_${newImage.name}`);
        await uploadBytes(imageRef, newImage);
        updatedImageUrl = await getDownloadURL(imageRef);
      } else if (positionImage === 'aucune' && imageUrl) {
        // Si on ne veut plus d'image, supprimer l'ancienne
        const storage = getStorage();
        const oldImageRef = ref(storage, imageUrl);
        await deleteObject(oldImageRef);
        updatedImageUrl = '';
      }

      const article = {
        titre,
        resume,
        contenuComplet,
        image: updatedImageUrl,
        positionImage: updatedImageUrl ? positionImage : 'aucune',
        dateModification: new Date()
      };

      const db = getFirestore();
      await updateDoc(doc(db, 'Articles', id), article);
      navigate('/mapage/articles');
    } catch (err) {
      console.error('Erreur:', err);
      setError('Erreur lors de la modification de l\'article');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ajout-article-container">
      <h2>Modifier l'article</h2>
      <form onSubmit={handleSubmit} className="article-form">
        <div className="form-group">
          <label htmlFor="titre">Titre</label>
          <input
            type="text"
            id="titre"
            value={titre}
            onChange={(e) => setTitre(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="resume">Résumé</label>
          <textarea
            id="resume"
            value={resume}
            onChange={(e) => setResume(e.target.value)}
            required
            rows={4}
          />
        </div>

        <div className="form-group">
          <label htmlFor="contenuComplet">Contenu complet</label>
          <textarea
            id="contenuComplet"
            value={contenuComplet}
            onChange={(e) => setContenuComplet(e.target.value)}
            required
            rows={8}
          />
        </div>

        <div className="form-group">
          <label>Image actuelle</label>
          {imageUrl && <img src={imageUrl} alt="Article" className="preview-image" style={{maxWidth: '200px'}} />}
        </div>

        <div className="form-group">
          <label>Nouvelle image</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="file-input"
          />
        </div>

        <div className="form-group">
          <label>Position de l'image</label>
          <select
            value={positionImage}
            onChange={(e) => setPositionImage(e.target.value as 'gauche' | 'droite' | 'aucune')}
          >
            <option value="aucune">Aucune image</option>
            <option value="gauche">À gauche</option>
            <option value="droite">À droite</option>
          </select>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/mapage/articles')}
            className="cancel-btn"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            className="submit-btn"
          >
            {loading ? 'Modification en cours...' : 'Modifier l\'article'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ModifierArticle;